import React from "react";
import PropTypes from "prop-types";
import { Formik } from "formik";
import * as Yup from "yup";
import Grid from "@mui/material/Grid";
import { get } from "lodash";
import { YesNoList } from "../../../../../../constants/options";

import { InlineRadioGroup, AutoSaveField as Field } from "../../../../../../components/Form";
import { getRequiredMessage } from "../../../../../../utils/getValidationMessage";
import { ContinueButton } from "../../../../../../components/Buttons/ContinueButton";
import { Divider } from "../../../../../../components/Divider";
import { ButtonWrapper, ComplianceHeader } from "./styled";
import { FormContainer } from "../CompanyBusinessRelationships/styled";
import { ScrollToError } from "components/ScrollToError";

const companyBusinessRelationshipsSchema = isCustomerNotSanctionCountries =>
  Yup.object().shape({
    CompanyHasHawala: Yup.string().max(10, "Maximum 10 characters allowed"),
    CompanyHasHawalaInterest: Yup.string().max(10, "Maximum 10 characters allowed"),
    CompanyDealingWithSanctionedCountries: Yup.string().max(10, "Maximum 10 characters allowed"),
    CompanyDealingWithSyriaOrIran: Yup.string().when("isCustomerNotSanctionCountries", {
      is: value => !isCustomerNotSanctionCountries,
      then: Yup.string() // NOSONAR
        .nullable()
        .required(getRequiredMessage("Company Dealing with Syria or Iran"))
        .max(10, "Maximum 10 characters allowed")
    }),

    CompanyHasAccountWithBSIOrBMI: Yup.string().max(10, "Maximum 10 characters allowed"),
    StakeholderHawalaLicensed: Yup.string().max(10, "Maximum 10 characters allowed")
  });
const intialSchema = {
  CompanyHasHawala: "",
  CompanyHasHawalaInterest: "",
  CompanyDealingWithSanctionedCountries: "",
  CompanyDealingWithSyriaOrIran: "",
  CompanyHasAccountWithBSIOrBMI: "",
  StakeholderHawalaLicensed: ""
};

export const ComplianceConfirmationComponent = ({ handleContinue, orgKYCDetails }) => {
  const isCustomerNotSanctionCountries = get(
    orgKYCDetails,
    "isCustomerNotSanctionCountries",
    false
  );

  return (
    <Formik
      initialValues={intialSchema}
      onSubmit={handleContinue}
      validationSchema={companyBusinessRelationshipsSchema(isCustomerNotSanctionCountries)}
      validateOnChange={false}
    >
      <FormContainer>
        <ScrollToError />
        <Grid container spacing={3}>
          <Grid item sm={10} xs={12}>
            <Field
              name="CompanyHasHawala"
              component={InlineRadioGroup}
              options={YesNoList}
              path="roProspect.organizationInfoRO.complianceConfirmation.involvedInHawalaRelation"
              label="Does the company or any sister company have a hawala business license, or are involved in any hawala relations?"
              InputProps={{
                inputProps: { tabIndex: 0 }
              }}
            />
          </Grid>
          <Grid item sm={10} xs={12}>
            <Field
              name="CompanyHasHawalaInterest"
              component={InlineRadioGroup}
              options={YesNoList}
              path="roProspect.organizationInfoRO.complianceConfirmation.involvedInHawalaLicensedComp"
              label="Does the company or any sister company maintain any relationship or interest in any Hawala licensed companies or exchange houses?"
              InputProps={{
                inputProps: { tabIndex: 0 }
              }}
            />
          </Grid>
          <Grid item sm={10} xs={12}>
            <Field
              name="CompanyDealingWithSanctionedCountries"
              component={InlineRadioGroup}
              options={YesNoList}
              path="roProspect.organizationInfoRO.complianceConfirmation.dealWithSanctionedCountries"
              label="Does the company or any sister company have dealing with sanctioned countries?"
              InputProps={{
                inputProps: { tabIndex: 0 }
              }}
            />
          </Grid>
          <Grid item sm={10} xs={12}>
            <Field
              name="CompanyDealingWithSyriaOrIran"
              component={InlineRadioGroup}
              options={YesNoList}
              path="roProspect.organizationInfoRO.complianceConfirmation.dealWithSyrianProduct"
              label="Does the company or any sister company have dealing with Syrian products or brands, or any other products that have origin in Iran or Syria?"
              InputProps={{
                inputProps: { tabIndex: 0 }
              }}
            />
          </Grid>
          <Grid item sm={10} xs={12}>
            <Field
              name="CompanyHasAccountWithBSIOrBMI"
              component={InlineRadioGroup}
              options={YesNoList}
              path="roProspect.organizationInfoRO.complianceConfirmation.activeAccountWithBSI"
              label="Does the company have any active accounts with BSI or BMI?"
              InputProps={{
                inputProps: { tabIndex: 0 }
              }}
            />
          </Grid>
          <Grid item sm={12} xs={12}>
            <Divider.Light />
          </Grid>
          <Grid item sm={10} xs={12}>
            <ComplianceHeader>Stakeholders</ComplianceHeader>
            <Field
              name="StakeholderHawalaLicensed"
              component={InlineRadioGroup}
              options={YesNoList}
              path="roProspect.organizationInfoRO.complianceConfirmation.signatoryInHawalaLicensedComp"
              label="Do any of the shareholders, signatories or their close relatives have any relationship or interest in any Hawala licensed companies, or exchange houses?"
              InputProps={{
                inputProps: { tabIndex: 0 }
              }}
            />
          </Grid>
        </Grid>

        <ButtonWrapper>
          <ContinueButton type="submit" />
        </ButtonWrapper>
      </FormContainer>
    </Formik>
  );
};

ComplianceConfirmationComponent.propTypes = {
  /**
   * Function to handle form submission and continue to next step
   */
  handleContinue: PropTypes.func.isRequired,

  /**
   * Organization KYC details containing compliance-related information
   */
  orgKYCDetails: PropTypes.shape({
    isCustomerNotSanctionCountries: PropTypes.bool
  })
};
